import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, Alert, Spinner } from "react-bootstrap";
import { getChoferes } from "../helpers/rutaChoferes";
import { postMovimientoExtraChofer } from "../helpers/rutaMovimientoExtraChofer";

const todayLocalISO = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const getChoferLabel = (c) => {
  if (!c) return "";
  const partes = [
    c.chofer,
    c.nombre,
    c.apellido,
    c.razonsocial,
    c.descripcion,
  ].filter(Boolean);
  return partes.join(" ").trim();
};

const normalizarChoferes = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp.choferes)) return resp.choferes;
  if (Array.isArray(resp.data?.choferes)) return resp.data.choferes;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

export default function ModalCobroPagoExtraChofer({
  show,
  setShow,
  onGuardado,
  choferInicial = "",
}) {
  const [choferes, setChoferes] = useState([]);
  const [cargandoChoferes, setCargandoChoferes] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const [formValues, setFormValues] = useState({
    chofer: "",
    fecha: todayLocalISO(),
    impacto: "RESTA", // RESTA baja el saldo | SUMA sube el saldo
    monto: "",
    mediopago: "EFECTIVO",
    observaciones: "",
  });

  const resetForm = () => {
    setError("");
    setFormValues({
      chofer: choferInicial || "",
      fecha: todayLocalISO(),
      impacto: "RESTA",
      monto: "",
      mediopago: "EFECTIVO",
      observaciones: "",
    });
  };

  const handleClose = () => {
    resetForm();
    setShow(false);
  };

  useEffect(() => {
    if (!show) return;

    const cargarChoferes = async () => {
      setCargandoChoferes(true);
      setError("");

      try {
        const resp = await getChoferes();

        if (!resp?.ok) {
          setError(resp?.error?.message || "No se pudieron cargar los choferes");
          return;
        }

        setChoferes(normalizarChoferes(resp));
      } catch (e) {
        setError("Error al cargar choferes");
      } finally {
        setCargandoChoferes(false);
      }
    };

    cargarChoferes();

    setFormValues((prev) => ({
      ...prev,
      chofer: choferInicial || prev.chofer || "",
      fecha: todayLocalISO(),
    }));
  }, [show, choferInicial]);

  const handleChange = ({ target }) => {
    const { name, value } = target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formValues.chofer) {
      setError("Seleccioná un chofer");
      return;
    }

    if (!formValues.fecha) {
      setError("Ingresá la fecha");
      return;
    }

    const monto = Number(formValues.monto || 0);
    if (monto <= 0) {
      setError("El monto debe ser mayor a 0");
      return;
    }

    if (!formValues.observaciones.trim()) {
      setError("Ingresá una observación");
      return;
    }

    const payload = {
      chofer: formValues.chofer,
      fecha: formValues.fecha,
      impacto: formValues.impacto, // SUMA o RESTA
      monto,
      mediopago: formValues.mediopago,
      observaciones: formValues.observaciones.trim(),
      estado: "activo",
    };

    setGuardando(true);
    setError("");

    try {
      const resp = await postMovimientoExtraChofer(payload);

      if (!resp?.ok) {
        setError(resp?.error?.message || "No se pudo guardar el movimiento extraordinario");
        return;
      }

      if (onGuardado) onGuardado(resp);
      handleClose();
    } catch (e) {
      setError("Ocurrió un error al guardar");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Cobro / Pago extraordinario</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Chofer</Form.Label>
                <Form.Select
                  name="chofer"
                  value={formValues.chofer}
                  onChange={handleChange}
                  disabled={cargandoChoferes || guardando}
                >
                  <option value="">Seleccione chofer</option>
                  {choferes
                    .filter((c) => c?.activo !== false)
                    .sort((a, b) =>
                      getChoferLabel(a).localeCompare(getChoferLabel(b), "es", {
                        sensitivity: "base",
                      })
                    )
                    .map((c) => (
                      <option key={c._id} value={c._id}>
                        {getChoferLabel(c)}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Fecha</Form.Label>
                <Form.Control
                  type="date"
                  name="fecha"
                  value={formValues.fecha}
                  onChange={handleChange}
                  disabled={guardando}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Impacto en saldo</Form.Label>
                <Form.Select
                  name="impacto"
                  value={formValues.impacto}
                  onChange={handleChange}
                  disabled={guardando}
                >
                  <option value="RESTA">RESTA AL SALDO</option>
                  <option value="SUMA">SUMA AL SALDO</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Monto</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  name="monto"
                  value={formValues.monto}
                  onChange={handleChange}
                  disabled={guardando}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Medio de pago</Form.Label>
                <Form.Select
                  name="mediopago"
                  value={formValues.mediopago}
                  onChange={handleChange}
                  disabled={guardando}
                >
                  <option value="EFECTIVO">EFECTIVO</option>
                  <option value="TRANSFERENCIA">TRANSFERENCIA</option>
                  <option value="CHEQUE">CHEQUE</option>
                  <option value="OTRO">OTRO</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={8}>
              <Form.Group>
                <Form.Label>Observaciones</Form.Label>
                <Form.Control
                  type="text"
                  name="observaciones"
                  value={formValues.observaciones}
                  onChange={handleChange}
                  disabled={guardando}
                  placeholder="Ej: Pago deuda vieja al Barbudo"
                />
              </Form.Group>
            </Col>
          </Row>

          <Alert variant="secondary" className="mb-0">
            <strong>RESTA AL SALDO</strong> baja la deuda pendiente.<br />
            <strong>SUMA AL SALDO</strong> aumenta la deuda pendiente.
          </Alert>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button variant="success" type="submit" disabled={guardando}>
            {guardando ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Guardando
              </>
            ) : (
              "Guardar"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}